import React from 'react';
import { AlertBanner, Spinner, Text } from '@spark';
import { blockingOf, continuingOf, countProcesses, mustWait, processDetails, theyFinish } from '../utils/busy';

const ANYWAY_WARNING =
    'Update anyway doesn’t wait: the running processes may fail and need to be started again.';

function Paragraph({ children }) {
    return (
        <Text as="p" variant="base-regular" color="secondary" className="platform-upgrader-paragraph">
            {children}
        </Text>
    );
}

function ProcessList({ processes, label }) {
    return (
        <ul className="platform-upgrader-processes" aria-label={label}>
            {processes.map((process, index) => (
                <li key={`${process.kind}-${process.name}-${index}`} className="platform-upgrader-process">
                    <Text as="span" variant="base-semibold" color="primary">
                        {process.name || process.kind}
                    </Text>
                    {processDetails(process) && (
                        <Text as="span" variant="detail-regular" color="secondary">
                            {processDetails(process)}
                        </Text>
                    )}
                </li>
            ))}
        </ul>
    );
}

function Summary({ busy, waiting }) {
    if (busy.failed) {
        return (
            <AlertBanner
                feedback="error"
                text={['Couldn’t check for running work.', busy.detail].filter(Boolean).join(' ')}
                showButton={false}
                showCloseButton={false}
            />
        );
    }
    const blocking = blockingOf(busy);
    if (!blocking.length) {
        return <Paragraph>{waiting ? 'Nothing is running any more.' : 'Nothing is running right now.'}</Paragraph>;
    }
    const running = `${countProcesses(blocking.length)} ${blocking.length === 1 ? 'is' : 'are'} running.`;
    const finish = theyFinish(blocking.length);
    return (
        <>
            <Paragraph>
                {waiting
                    ? `${running} The update starts once ${finish}.`
                    : `${running} The update can wait until ${finish}; nobody can start new work meanwhile.`}
            </Paragraph>
            <ProcessList processes={blocking} label="Running processes" />
        </>
    );
}

/**
 * What an update waits for, from ``GET busy``: the running processes with who started them and when,
 * then the work that continues after the update. ``waiting`` words it for an update that already waits.
 * While it would wait, a warning offers Update anyway.
 */
export default function BusyDetails({ busy, waiting = false, onUpdateAnyway }) {
    if (!busy || busy.loading) {
        return (
            <div className="platform-upgrader-busy-checking">
                <Spinner size="small" aria-label="Checking for running work" />
                <Text as="span" variant="base-regular" color="secondary">
                    Checking for running work…
                </Text>
            </div>
        );
    }
    const continuing = continuingOf(busy);
    return (
        <div className="platform-upgrader-busy">
            <Summary busy={busy} waiting={waiting} />
            {continuing.length > 0 && <ProcessList processes={continuing} label="Continues after the update" />}
            {mustWait(busy) && (
                <AlertBanner
                    feedback="warning"
                    text={ANYWAY_WARNING}
                    buttonLabel="Update anyway"
                    onActionClick={onUpdateAnyway}
                    showCloseButton={false}
                />
            )}
        </div>
    );
}
